/**
 * The Portal access slide-over — plan §6.2, drawn in the feature inventory's
 * `profiles-contact.png` breakdown.
 *
 * Two rules from the addendum shape it rather than decorate it:
 *
 * §G.6 — a contact who is not bill-to on a property sees no billing amounts at
 * all. So "See invoices" and "Pay and save a card" are not merely off for such
 * a contact, they are unavailable, with the reason on the row. The mockup's
 * older "totals only" note is superseded and is not reproduced here.
 *
 * Properties are not chosen in this panel. They follow the contact's links, so
 * the "Can see" section lists them read-only — one place to change who is on a
 * property, and it is the property.
 */
import { useState } from 'react'
import { SlideOver } from './overlays.js'
import { Button, SectionLabel, StatusChip, Switch, Tag } from './primitives.js'
import { formatDocumentDate, formatRelative } from '../lib/format.js'
import { request } from '../lib/api.js'

export interface PortalAccessRecord {
  id: string
  permissions: Record<string, boolean>
  invitedAt: string | null
  lastLoginAt: string | null
  revokedAt: string | null
  version: number
}

export interface PortalLinkedProperty {
  id: string
  propertyName: string
  role: string
  isBillTo: boolean
}

export interface PortalAccessSlideOverProps {
  open: boolean
  onClose: () => void
  contact: { id: string; firstName: string; lastName: string; email: string | null }
  access: PortalAccessRecord | null
  properties: PortalLinkedProperty[]
  onSaved: () => void
}

/** The permission rows, in the order the mockup lists them. */
const PERMISSIONS = [
  {
    key: 'approve_quotes',
    label: 'Approve quotes and sign',
    help: 'Options, add-ons, e-signature',
    billToOnly: false,
  },
  {
    key: 'request_service',
    label: 'Request service',
    help: 'Creates a Requested job with photos',
    billToOnly: false,
  },
  {
    key: 'view_visits',
    label: 'See visits, photos and crew notes',
    help: 'Past and upcoming, per property',
    billToOnly: false,
  },
  {
    key: 'view_invoices',
    label: 'See invoices',
    help: 'View only',
    billToOnly: true,
  },
  {
    key: 'pay_invoices',
    label: 'Pay and save a card',
    help: 'Card on file for deposits and auto-pay',
    billToOnly: true,
  },
] as const

function statusOf(access: PortalAccessRecord | null): {
  label: string
  status: 'completed' | 'scheduled' | 'requested'
} {
  if (!access || access.revokedAt) return { label: 'No access', status: 'requested' }
  if (access.lastLoginAt) return { label: 'Active', status: 'completed' }
  return { label: 'Invited', status: 'scheduled' }
}

export function PortalAccessSlideOver({
  open, onClose, contact, access, properties, onSaved,
}: PortalAccessSlideOverProps) {
  const [permissions, setPermissions] = useState<Record<string, boolean>>(
    access?.permissions ?? { view_visits: true, request_service: true })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [link, setLink] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const isBillToSomewhere = properties.some((property) => property.isBillTo)
  const state = statusOf(access)
  const name = `${contact.firstName} ${contact.lastName}`.trim()

  async function save(invite: boolean) {
    setBusy(true)
    setError(null)
    try {
      const saved = await request<{ id: string; link: string | null }>(
        `/contacts/${contact.id}/portal-access`,
        { method: 'PUT', body: { permissions, invite } })
      onSaved()
      /*
       * An invite comes back with the sign-in link, because email is not
       * connected yet and a link nobody was given is not access. So the panel
       * stays open holding it, rather than closing on a grant that reached
       * nobody. A save with no invite has nothing to hand over and closes.
       */
      if (saved.link) setLink(saved.link)
      else onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  async function revoke() {
    setBusy(true)
    setError(null)
    try {
      await request(`/contacts/${contact.id}/portal-access`, { method: 'DELETE' })
      onSaved()
      onClose()
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'That could not be revoked.')
    } finally {
      setBusy(false)
    }
  }

  async function copy(): Promise<void> {
    if (!link) return
    try {
      await navigator.clipboard.writeText(link)
      setCopied(true)
    } catch {
      // A browser that will not hand over the clipboard without a gesture it
      // trusts. The link is on screen and selectable, which is the fallback.
      setCopied(false)
    }
  }

  if (link) {
    return (
      <SlideOver
        open={open}
        onClose={onClose}
        title="Portal access"
        width={440}
        footer={
          <div className="flex w-full justify-end">
            <Button variant="primary" onClick={onClose}>Done</Button>
          </div>
        }
      >
        <div className="flex flex-col gap-3">
          <div>
            <span className="text-card font-semibold text-text">Send {name} this link</span>
            <p className="m-0 mt-1 text-cell text-text-muted">
              It signs them in once, expires in seven days, and works on one device only.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              readOnly
              aria-label="Sign-in link"
              value={link}
              onFocus={(event) => event.currentTarget.select()}
              className="min-w-0 flex-1 rounded-field border border-line bg-field px-2 py-1.5
                         text-cell text-text"
            />
            <Button onClick={copy}>{copied ? 'Copied' : 'Copy'}</Button>
          </div>
        </div>
      </SlideOver>
    )
  }

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="Portal access"
      width={440}
      footer={
        <div className="flex w-full items-center justify-between gap-2">
          {access && !access.revokedAt
            ? (
              <Button variant="danger" disabled={busy} onClick={revoke}>
                Revoke
              </Button>
            )
            : <span />}
          <div className="flex gap-2">
            {/*
              * A link is single-use and good for seven days, so an invited
              * contact who lost theirs needs another one. Without this the
              * only way back in was to revoke and re-grant.
              */}
            {access?.invitedAt && (
              <Button disabled={busy || !contact.email} onClick={() => save(true)}>
                New link
              </Button>
            )}
            <Button disabled={busy} onClick={onClose}>Cancel</Button>
            <Button variant="primary" disabled={busy || !contact.email} onClick={() => save(!access?.invitedAt)}>
              {access?.invitedAt ? 'Save' : 'Invite'}
            </Button>
          </div>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-1 flex items-center gap-2">
            <span className="text-card font-semibold text-text">{name}</span>
            <StatusChip group="job" status={state.status}>{state.label}</StatusChip>
          </div>
          {contact.email
            ? (
              <p className="m-0 text-cell text-text-muted">
                Signs in with a link sent to {contact.email} — no password.
              </p>
            )
            : (
              <p className="m-0 text-cell text-danger">
                This contact has no email address, so there is nowhere to send a sign-in link.
              </p>
            )}
          {access && (
            <p className="m-0 mt-1 text-sub text-text-faint">
              {access.invitedAt ? `Invited ${formatDocumentDate(access.invitedAt)}` : 'Not invited yet'}
              {' · '}
              {access.lastLoginAt ? `Last signed in ${formatRelative(access.lastLoginAt)}` : 'Never signed in'}
            </p>
          )}
        </div>

        <div>
          <SectionLabel className="mb-1.5 block">Can see</SectionLabel>
          {properties.length === 0
            ? (
              <p className="m-0 text-cell text-text-muted">
                This contact is not linked to a property yet, so there is nothing to show them.
              </p>
            )
            : (
              <ul className="m-0 list-none space-y-1 p-0">
                {properties.map((property) => (
                  <li key={property.id} className="flex items-center gap-2 text-cell text-text">
                    <span className="truncate">{property.propertyName}</span>
                    <Tag>{property.role}</Tag>
                    {property.isBillTo && <Tag>Bill to</Tag>}
                  </li>
                ))}
              </ul>
            )}
          <p className="m-0 mt-1.5 text-sub text-text-faint">
            Properties follow the links on this contact. New links are added automatically.
          </p>
        </div>

        <div>
          <SectionLabel className="mb-1.5 block">Can do</SectionLabel>
          <div className="flex flex-col gap-2.5">
            {PERMISSIONS.map((permission) => {
              // Addendum §G.6: not bill-to means no billing amounts at all, so
              // the switch is unavailable rather than merely off.
              const blocked = permission.billToOnly && !isBillToSomewhere
              return (
                // Switch takes `label` as an aria-label only, so the visible
                // text is drawn here beside it.
                <div key={permission.key} className="flex items-start gap-2.5">
                  <div className="pt-0.5">
                    <Switch
                      checked={blocked ? false : Boolean(permissions[permission.key])}
                      disabled={blocked}
                      hideLabel
                      onChange={(value) => setPermissions((current) => ({
                        ...current,
                        [permission.key]: value,
                        // Paying implies seeing what you are paying.
                        ...(permission.key === 'pay_invoices' && value
                          ? { view_invoices: true, manage_payment_methods: true }
                          : {}),
                      }))}
                      label={permission.label}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-cell text-text">{permission.label}</p>
                    <p className="m-0 text-sub text-text-faint">
                      {permission.help}
                      {blocked && ' · off — not bill-to on any property'}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {error && <p className="m-0 text-cell text-danger">{error}</p>}
      </div>
    </SlideOver>
  )
}
